import { useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { useStore } from '../store/useStore';

const LIFF_ID = import.meta.env.VITE_LIFF_ID || '';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

export function useLiff() {
  const {
    isInitialized,
    isAuthenticated,
    setInitialized,
    setAuthenticated,
    setLiffError,
    setUser,
    setTodayLogs,
    setStats,
  } = useStore();

  // Handle user login and sync with backend
  const handleLogin = useCallback(async () => {
    try {
      const profile = await liff.getProfile();

      if (!profile) {
        throw new Error('Failed to get profile');
      }

      // Sync with backend
      try {
        const response = await fetch(`${API_BASE_URL}/user/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: profile.userId,
            displayName: profile.displayName,
            pictureUrl: profile.pictureUrl,
            statusMessage: profile.statusMessage,
          }),
        });

        if (response.ok) {
          const data = await response.json();

          // Always use LINE profile as source of truth for name/picture
          const userWithProfile = {
            ...data.user,
            displayName: profile.displayName || data.user.displayName,
            pictureUrl: profile.pictureUrl || data.user.pictureUrl || null,
          };

          // If viewer → load OWNER's data and merge owner's settings
          const isViewerUser = data.user.role === 'viewer' && data.user.ownerUserId;
          const targetId = isViewerUser ? data.user.ownerUserId : data.user.id;

          if (isViewerUser) {
            // Fetch owner's profile to get their settings (limits, smoking type, etc.)
            try {
              const ownerRes = await fetch(`${API_BASE_URL}/user/${data.user.ownerUserId}`);
              if (ownerRes.ok) {
                const ownerData = await ownerRes.json();
                const owner = ownerData.user;
                // Merge owner's smoking settings into viewer's user object
                // Keep viewer's own identity (name, picture) but use owner's settings
                const mergedUser = {
                  ...userWithProfile,
                  dailyPuffLimit: owner.dailyPuffLimit,
                  smokingType: owner.smokingType,
                  smokingDuration: owner.smokingDuration,
                  dailyAmount: owner.dailyAmount,
                  monthlyCost: owner.monthlyCost,
                  costPerPuff: owner.costPerPuff,
                  nicotinePerPuff: owner.nicotinePerPuff,
                  puffsPerPod: owner.puffsPerPod,
                  podPrice: owner.podPrice,
                  mode: owner.mode,
                  quitDate: owner.quitDate,
                };
                setUser(mergedUser, data.isNew);
              } else {
                setUser(userWithProfile, data.isNew);
              }
            } catch {
              setUser(userWithProfile, data.isNew);
            }
          } else {
            setUser(userWithProfile, data.isNew);
          }

          setAuthenticated(true);

          await loadTodayLogs(targetId);
          await loadStats(targetId);
          return;
        }
      } catch (apiError) {
        console.warn('API sync failed, using local profile:', apiError);
      }

      // Fallback: use profile data locally even if API fails
      setUser({
        id: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl || null,
        statusMessage: profile.statusMessage || null,
        mode: 'reduce',
        dailyPuffLimit: 20,
        costPerPuff: 0.5,
        nicotinePerPuff: 0.1,
        onboardingCompleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as any, false);
      setAuthenticated(true);
    } catch (error) {
      console.error('Login error:', error);
      setLiffError(error instanceof Error ? error.message : 'Login failed');
    }
  }, []);

  // Initialize LIFF SDK
  useEffect(() => {
    const initLiff = async () => {
      // If LIFF_ID is set, initialize LIFF and force login
      if (LIFF_ID) {
        try {
          await liff.init({ liffId: LIFF_ID });
          setInitialized(true);

          if (!liff.isLoggedIn()) {
            console.log('LIFF: Not logged in, redirecting to LINE Login...');
            liff.login({ redirectUri: window.location.href });
            return;
          }

          console.log('LIFF: Already logged in, fetching profile...');
          await handleLogin();
        } catch (error) {
          console.error('LIFF initialization failed:', error);
          setLiffError(error instanceof Error ? error.message : 'Failed to initialize LIFF');
          setInitialized(true);
        }
        return;
      }

      // No LIFF_ID => Mock mode for development
      console.warn('LIFF ID not configured, running in mock mode');
      setUser({
        id: 'mock-user-id',
        displayName: 'ผู้ใช้ทดสอบ',
        pictureUrl: null,
        statusMessage: null,
        mode: 'reduce' as const,
        dailyPuffLimit: 20,
        costPerPuff: 0.5,
        nicotinePerPuff: 0.1,
        onboardingCompleted: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as any, false);
      setAuthenticated(true);
      setInitialized(true);
    };

    initLiff();
  }, []);

  // Login with LINE
  const login = useCallback(async () => {
    try {
      if (!LIFF_ID) {
        // Mock login for development
        const mockUser = {
          id: 'mock-user-id',
          displayName: 'ผู้ใช้ทดสอบ',
          pictureUrl: undefined,
          statusMessage: undefined,
          mode: 'reduce' as const,
          dailyPuffLimit: 20,
          costPerPuff: 0.5,
          nicotinePerPuff: 0.1,
          onboardingCompleted: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setUser(mockUser, true);
        setAuthenticated(true);
        return;
      }

      if (!liff.isInClient() && !liff.isLoggedIn()) {
        liff.login({ redirectUri: window.location.href });
      } else if (liff.isLoggedIn()) {
        await handleLogin();
      }
    } catch (error) {
      console.error('Login error:', error);
      setLiffError(error instanceof Error ? error.message : 'Login failed');
    }
  }, [handleLogin]);

  // Logout
  const logout = useCallback(() => {
    if (LIFF_ID && liff.isLoggedIn()) {
      liff.logout();
    }
    setUser(null);
    setAuthenticated(false);
    setTodayLogs([]);
    setStats(null);
  }, []);

  // Load today's logs
  const loadTodayLogs = async (userId: string) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const response = await fetch(
        `${API_BASE_URL}/logs/${userId}?startDate=${today.toISOString()}&limit=1000`
      );
      if (response.ok) {
        const data = await response.json();
        setTodayLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  // Load stats
  const loadStats = async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/stats/${userId}?period=week`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Refresh data
  const refreshData = useCallback(async () => {
    const user = useStore.getState().user;
    if (user) {
      // If viewer → refresh OWNER's data
      const targetId = (user as any).role === 'viewer' && (user as any).ownerUserId
        ? (user as any).ownerUserId
        : user.id;
      await loadTodayLogs(targetId);
      await loadStats(targetId);
    }
  }, []);

  // Get LIFF context
  const getContext = useCallback(() => {
    if (LIFF_ID) return liff.getContext();
    return null;
  }, []);

  // Close LIFF app
  const closeApp = useCallback(() => {
    if (LIFF_ID) liff.closeWindow();
  }, []);

  // Send messages (only in LIFF app)
  const sendMessage = useCallback(async (text: string) => {
    if (LIFF_ID && liff.isInClient()) {
      await liff.sendMessages([{ type: 'text', text }]);
    }
  }, []);

  // Share target picker
  const shareTargetPicker = useCallback(async (messages: unknown[]) => {
    if (LIFF_ID && liff.isApiAvailable('shareTargetPicker')) {
      await liff.shareTargetPicker(messages as Parameters<typeof liff.shareTargetPicker>[0]);
    }
  }, []);

  return {
    isInitialized,
    isAuthenticated,
    isInClient: LIFF_ID && isInitialized ? liff.isInClient?.() || false : false,
    isLoggedIn: LIFF_ID && isInitialized ? liff.isLoggedIn?.() || false : false,
    login,
    logout,
    refreshData,
    getContext,
    closeApp,
    sendMessage,
    shareTargetPicker,
  };
}
